/**
 * TITAM AI - Google Ads - Página de Projeções (Por Período)
 * Arquitetura Centralizada: Lógica Matemática e Contagem de Dias 100% no Backend.
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { DataTable } from '../../components/DataTable.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'

// Colunas alinhadas exatamente com o padrão das outras telas (Métricas nas colunas)
const COLUMNS = [
  { key: 'label', label: 'Período', locked: true, sortable: false },
  { key: 'cost', label: 'Investimento', format: 'currency', align: 'right', sortable: false },
  { key: 'impressions', label: 'Impressões', format: 'number', align: 'right', sortable: false },
  { key: 'cpm', label: 'CPM', format: 'currency', align: 'right', sortable: false },
  { key: 'ctr', label: 'CTR', format: 'percent', align: 'right', sortable: false },
  { key: 'clicks', label: 'Cliques', format: 'number', align: 'right', sortable: false },
  { key: 'cpc', label: 'CPC', format: 'currency', align: 'right', sortable: false },
  { key: 'clickToVisit', label: 'Click→Visita %', format: 'percent', align: 'right', sortable: false },
  { key: 'visits', label: 'Visitas', format: 'number', align: 'right', sortable: false },
  { key: 'cpv', label: 'CPV', format: 'currency', align: 'right', sortable: false },
  { key: 'visitToWhatsapp', label: 'Cliques Whats %', format: 'percent', align: 'right', sortable: false },
  { key: 'visitToForm', label: 'Forms %', format: 'percent', align: 'right', sortable: false },
  { key: 'whatsappClicks', label: 'Total CW', format: 'number', align: 'right', sortable: false },
  { key: 'cpcw', label: 'CPCW', format: 'currency', align: 'right', sortable: false },
  { key: 'forms', label: 'Total Forms', format: 'number', align: 'right', sortable: false },
  { key: 'cpf', label: 'CPF', format: 'currency', align: 'right', sortable: false },
  { key: 'leads', label: 'Leads', format: 'number', align: 'right', sortable: false },
  { key: 'leadToContact', label: 'Contatos %', format: 'percent', align: 'right', sortable: false },
  { key: 'leadToForm', label: 'Forms %', format: 'percent', align: 'right', sortable: false },
  { key: 'contacts', label: 'Total CC', format: 'number', align: 'right', sortable: false },
  { key: 'cpcc', label: 'Custo CC', format: 'currency', align: 'right', sortable: false },
  { key: 'cpfTotal', label: 'CPF (Total Forms)', format: 'currency', align: 'right', sortable: false },
  { key: 'qualifiedLeads', label: 'LQ', format: 'number', align: 'right', sortable: false },
  { key: 'lqToSale', label: 'LQ→Venda %', format: 'percent', align: 'right', sortable: false },
  { key: 'cplq', label: 'Custo LQ', format: 'currency', align: 'right', sortable: false },
  { key: 'sales', label: 'Vendas', format: 'number', align: 'right', sortable: false },
  { key: 'cac', label: 'CAC', format: 'currency', align: 'right', sortable: false },
  { key: 'roas', label: 'ROAS', format: 'decimal', align: 'right', sortable: false },
]

export default function ProjectionsPage({ accounts = [] }) {
  const { toast, activeClient } = useApp()
  const activeClientId = activeClient?.id ?? null

  // Helpers de Data
  const getToday = () => new Date().toISOString().split('T')[0]
  const getFirstDayOfMonth = () => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]
  }

  // Estados do Simulador
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [baseStart, setBaseStart] = useState(getFirstDayOfMonth())
  const [baseEnd, setBaseEnd] = useState(getToday())

  const [projStart, setProjStart] = useState(getToday())
  const [projEnd, setProjEnd] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 15) // Padrão: próximos 15 dias
    return d.toISOString().split('T')[0]
  })

  const [includeWeekends, setIncludeWeekends] = useState(true)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  // Fase 5 (B2): proteção contra stale results em troca rápida de cliente.
  const genRef = useRef(0)

  const fetchSimulation = async () => {
    const myGen = ++genRef.current
    setLoading(true)
    try {
      /**
       * CORREÇÃO DOS PARÂMETROS:
       * O backend espera 'base_end_custom' e 'base_end_flag'
       */
      const params = {
        base_start: baseStart,
        base_end_flag: 'custom',       // Indica ao FastAPI para usar a data customizada
        base_end_custom: baseEnd,      // Passa o valor do estado baseEnd
        proj_start: projStart,
        proj_end: projEnd,
        include_weekends: includeWeekends
      }

      if (selectedAccountId) {
        params.account_id = selectedAccountId
      }

      // Chama o helper centralizado (api.js) que injeta o Token do Supabase
      const json = await api.getPeriodProjection(params)
      if (myGen !== genRef.current) return
      setData(json)
      toast('Pace calculado com sucesso!', 'success')
    } catch (err) {
      if (myGen !== genRef.current) return
      console.error(err)
      toast(err.message || 'Erro ao simular projeção', 'error')
      setData(null)
    } finally {
      if (myGen === genRef.current) setLoading(false)
    }
  }

  // Pivoteamento de dados para a DataTable
  const tableRows = useMemo(() => {
    if (!data || !data.rows) return []
    return data.rows
  }, [data])

  // Fase 5 (B2): troca de cliente invalida `selectedAccountId` (a ad_account
  // pode não pertencer ao novo cliente; backend retornaria 404). Resetamos
  // para "Todas as contas" e limpamos `data` para evitar render de stale.
  // O useEffect abaixo re-dispara `fetchSimulation` quando `selectedAccountId`
  // volta para '' ou quando activeClientId muda.
  useEffect(() => {
    if (activeClientId === null) return
    setSelectedAccountId('')
    setData(null)
  }, [activeClientId])

  useEffect(() => {
    fetchSimulation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, activeClientId])

  return (
    <div className="ga-page">
      <div className="projections-simulator">
        <div className="simulator-header">
          <h3>Simulador de Pace</h3>
          <p>Extrapolação matemática com base em volume diário histórico. Limite máximo de projeção: 6 meses.</p>
        </div>

        <div className="simulator-controls" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'end' }}>
          
          <div className="control-group">
            <label>Conta</label>
            <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}>
              <option value="">Todas as contas</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Início Base (Realizado)</label>
            <input className="input" type="date" value={baseStart} onChange={(e) => setBaseStart(e.target.value)} max={getToday()} />
          </div>

          <div className="control-group">
            <label>Fim Base (Realizado)</label>
            <input className="input" type="date" value={baseEnd} onChange={(e) => setBaseEnd(e.target.value)} max={getToday()} />
          </div>

          <div className="control-group">
            <label>Início da Projeção</label>
            <input className="input" type="date" value={projStart} onChange={(e) => setProjStart(e.target.value)} />
          </div>

          <div className="control-group">
            <label>Fim da Projeção</label>
            <input className="input" type="date" value={projEnd} onChange={(e) => setProjEnd(e.target.value)} />
          </div>

          <div className="control-group">
            <label>Contar Finais de Semana?</label>
            <label className="toggle-switch" style={{ height: '38px' }}>
              <input
                type="checkbox"
                checked={includeWeekends}
                onChange={(e) => setIncludeWeekends(e.target.checked)}
              />
              <span className="toggle-slider" />
              <span className="toggle-switch-text">{includeWeekends ? 'Sim' : 'Não'}</span>
            </label>
          </div>

          <button
            className={`btn-primary ${loading ? 'loading' : ''}`}
            onClick={fetchSimulation}
            disabled={loading}
            style={{ height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, lineHeight: 1 }}
          >
            <Icon n={loading ? "clock" : "trend_up"} s={14} />
            Calcular Pace
          </button>
        </div>
      </div>

      <div className="table-section" style={{ marginTop: '24px' }}>
        <div className="table-section-header">
          <div>
            <div className="table-section-title">Resultados do Pace Calculado</div>
            {data && data.info && (
              <div className="table-section-subtitle" style={{ color: '#006064', fontWeight: '500' }}>
                Base histórica: {data.info.base_start} até {data.info.base_end} ({data.info.days_base} dias úteis aplicados). 
                Projetando para {data.info.days_proj} dias.
              </div>
            )}
          </div>
        </div>
        
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Cruzando dados operacionais no servidor...</div>
        ) : data ? (
          <DataTable
            columns={COLUMNS}
            data={tableRows}
            striped
            hoverable
            emptyText="Nenhum dado válido processado."
          />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Configure o período no simulador acima e clique em "Calcular Pace".
          </div>
        )}
      </div>
    </div>
  )
}